import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import type { CardPayload, CardholderSummary } from '@ankor-io/common/types/cards'

import { useCard } from '@/hooks/useCard'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/ui-components/Dialog'
import Loading from '@/ui-components/Loading'
import { StepperWithNumbers } from '@/ui-components/Stepper'

import { CardLimitsStep } from './IssueCard/CardLimitsStep'
import { CardNicknameStep } from './IssueCard/CardNicknameStep'
import { UserSelectStep } from './IssueCard/UserSelectStep'
import { type CardData, DEFAULT_CARD_DATA } from './IssueCard/types'

type IssueCardModalProps = {
  isOpen: boolean
  onClose: () => void
  preselectedCardholder?: CardholderSummary
}

export const IssueCardModal = ({ isOpen, onClose, preselectedCardholder }: IssueCardModalProps) => {
  const [step, setStep] = useState(0)
  const [cardData, setCardData] = useState<CardData>(DEFAULT_CARD_DATA)
  const [cardholders, setCardholders] = useState<CardholderSummary[]>([])
  const [invitedUser, setInvitedUser] = useState('')
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const { setCard } = useCard()

  const [isSaving, setIsSaving] = useState(false)
  const [areLimitsValid, setAreLimitsValid] = useState(true)
  const [isLoadingCardholders, setIsLoadingCardholders] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (preselectedCardholder?.id) {
      setCardData({
        ...DEFAULT_CARD_DATA,
        userEmail: preselectedCardholder.identity.email,
        userFullName: preselectedCardholder.identity.firstName + ' ' + preselectedCardholder.identity.lastName,
      })
    } else {
      const getCardholders = async () => {
        setIsLoadingCardholders(true)
        try {
          const res = await fetch(
            `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/cardholders`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getAccessToken()}`,
              },
            },
          )

          if (!res.ok) {
            throw new Error('Failed to fetch cardholders')
          }

          const data = await res.json()
          setCardholders(data.cardholders || [])
        } catch (error) {
          console.error(error)
        } finally {
          setIsLoadingCardholders(false)
        }
      }
      getCardholders()
    }
  }, [isOpen, preselectedCardholder, getAccessToken, activeOrganizationId, activeConnectedAccountId])

  const handleModalClose = useCallback(() => {
    onClose()
    // Timeout for visual - otherwise upon closing the modal the modal display will change before it is closed
    setTimeout(() => {
      resetState()
    }, 500)
  }, [onClose])

  const resetState = () => {
    setCardData(DEFAULT_CARD_DATA)
    setInvitedUser('')
    setStep(0)
  }

  const handleUserEmailChange = useCallback(
    (email: string) => {
      const selectedCardholder = cardholders?.find((cardholder) => cardholder.identity.email === email)
      setInvitedUser(email || '')
      setCardData((prev) => ({
        ...prev,
        userEmail: email,
        userFullName: selectedCardholder
          ? selectedCardholder.identity.firstName + ' ' + selectedCardholder.identity.lastName
          : '',
      }))
    },
    [cardholders],
  )

  const handlePrev = useCallback(() => {
    setStep((prev) => prev - 1)
  }, [])

  const setIsNextButtonDisabled = () => {
    switch (step) {
      case 0:
        return !cardData.userEmail
      case 1:
        return !cardData.userFullName
      case 2:
        return false // optional field
      default:
        return false
    }
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (step === 2) {
        const createCard = async () => {
          setIsSaving(true)
          const limitsWithoutRank = cardData.limits.map((limit) => ({
            amount: limit.amount,
            interval: limit.interval,
          }))

          const payload = {
            currency: cardData.currency,
            nick_name: cardData.nickName,
            limits: limitsWithoutRank,
          }

          // Determine the cardholder ID based on preselected cardholder or selected email
          const cardholderId =
            preselectedCardholder?.id ||
            cardholders?.find((cardholder) => cardholder.identity.email === cardData.userEmail)?.id

          // If no cardholder ID found and we have an invited user email, we need to create a cardholder first
          if (!cardholderId && invitedUser) {
            // For now, we'll throw an error since creating a cardholder requires additional information
            // that's not available in this flow (like address, date of birth, etc.)
            throw new Error('Cannot create card for invited user. Please add the user as a cardholder first.')
          }

          if (!cardholderId) {
            throw new Error('No cardholder ID found')
          }

          const res = await fetch(
            `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/cardholder/${cardholderId}/card`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getAccessToken()}`,
              },
              body: JSON.stringify(payload),
            },
          )

          if (!res.ok) {
            setIsSaving(false)
            throw new Error('Failed to create card')
          }

          const card = (await res.json()) as CardPayload
          setCard(card)

          setIsSaving(false)
          handleModalClose()
        }

        toast.promise(createCard(), {
          loading: 'Creating card...',
          success: 'Card created successfully',
          error: 'Failed to create card',
        })
      } else {
        setStep((prev) => prev + 1)
      }
    },
    [
      step,
      cardData.limits,
      cardData.currency,
      cardData.nickName,
      cardData.userEmail,
      preselectedCardholder?.id,
      cardholders,
      invitedUser,
      activeOrganizationId,
      activeConnectedAccountId,
      getAccessToken,
      setCard,
      handleModalClose,
    ],
  )

  const renderStepComponent = () => {
    if (step === 0) {
      return isLoadingCardholders ? (
        <Loading />
      ) : (
        <UserSelectStep
          value={cardData.userEmail}
          invitedUser={invitedUser}
          setInvitedUser={handleUserEmailChange}
          cardholders={cardholders}
          preselectedCardholder={preselectedCardholder}
        />
      )
    } else if (step === 1) {
      return (
        <CardNicknameStep
          cardData={cardData}
          setCardData={setCardData}
        />
      )
    } else if (step === 2) {
      return (
        <CardLimitsStep
          cardData={cardData}
          setCardData={setCardData}
          setAreLimitsValid={setAreLimitsValid}
        />
      )
    }
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleModalClose}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Issue New Card</span>
          <Button
            plain
            onClick={handleModalClose}
            data-testid="x-button"
          >
            <X data-slot="icon" />
          </Button>
        </div>
      </DialogTitle>
      <DialogDescription>Fill the steps to issue a card to a user and set rules.</DialogDescription>

      <DialogBody>
        <StepperWithNumbers
          curStep={step}
          steps={[1, 2, 3]}
        />
        <form
          id="card-form"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {renderStepComponent()}
        </form>
      </DialogBody>

      <DialogActions>
        <div className="flex gap-x-2 w-full justify-between items-center">
          {step === 0 ? (
            <Button
              outline
              type="button"
              onClick={handleModalClose}
            >
              Close
            </Button>
          ) : (
            <Button
              outline
              type="button"
              onClick={handlePrev}
            >
              Back
            </Button>
          )}
          {step < 2 ? (
            <>
              <Button
                color="primary"
                type="submit"
                form="card-form"
                className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                disabled={setIsNextButtonDisabled()}
              >
                Next
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              color="primary"
              form="card-form"
              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                isSaving ||
                !cardData.currency ||
                cardData.limits.some((limit) => limit.amount === undefined || limit.interval === undefined) ||
                !areLimitsValid
              }
            >
              {isSaving ? 'Saving...' : 'Finish'}
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  )
}

export default IssueCardModal
